/**
 * `internalSay.ts` — the `INTERNAL SAY:` speech command, in its one place
 * (web-only form; the base prompt's Internal Speech Messages section is shared
 * with Unity, which only ever sends the plain one).
 *
 * The plain form tells the model to speak a line verbatim, and that section
 * forbids translating it. A learner who asked for another language
 * (`IWorkflowRunModel.speechLanguage`) gets the TAGGED form instead —
 * `INTERNAL SAY (in Spanish): …` — which the same section defines as "say this
 * in that language". The language rides the message itself rather than a rule
 * elsewhere in the prompt: the 2026-10-11 trace shows that a request the model
 * has to remember from earlier in the conversation loses to a CRITICAL rule
 * about the message in front of it about half the time
 * (`Campfire_Web_AI.md` → fix the input, not the rule).
 */

const K_INTERNAL_SAY = "INTERNAL SAY";

/**
 * The command for one line: plain, or tagged with the language to speak it in.
 * `language` is the TRANSLATION language — null when the line is to be spoken
 * as written (`IWorkflowRunModel.translationLanguage` decides) — and
 * `writtenIn` is the language the line is written in, which the note forbids.
 *
 * ⚠ The bracketed note is MEASURED, not decoration. With the tag alone the
 * line came back in English 6 times in 20 when Spark had already spoken two
 * English narrations after two requests (run E of the 2026-10-11 trace,
 * replayed on `gpt-realtime-2.1`): the model copies its own last lines. The
 * note in the message itself made it 20/20, and so did a system message just
 * before it; this one needs no second conversation item. The base prompt says
 * the note is not part of the line.
 */
export function formatInternalSay(
  text: string,
  language: string | null = null,
  writtenIn = "English",
): string {
  return language
    ? `${K_INTERNAL_SAY} (in ${language}): [Say this line in ${language}, translated faithfully - never in ${writtenIn}.] ${text}`
    : `${K_INTERNAL_SAY}: ${text}`;
}

/** Either form: `INTERNAL SAY:` or `INTERNAL SAY (in <language>):`. */
export function isInternalSay(text: string): boolean {
  return /^INTERNAL SAY(?: \(in [^)]*\))?:/.test(text);
}
